import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useStateValue } from './state'

const Dropdown = ({ trigger, children, isOpen, onClose }) => {
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose()
      }
    }

    const handleEscape = event => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  return (
    <div ref={dropdownRef} className="position-relative d-inline-block">
      {trigger}
      {isOpen && (
        <div
          className="position-absolute bg-white"
          style={styles.dropdownPanel}
        >
          {children}
        </div>
      )}
    </div>
  )
}

Dropdown.propTypes = {
  trigger: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

const MenuItem = ({ onClick, href, children }) => {
  const handleClick = e => {
    if (onClick) {
      e.preventDefault()
      onClick(e)
    }
  }

  const [isHovered, setIsHovered] = React.useState(false)

  if (href) {
    return (
      <a
        href={href}
        className="d-flex align-items-center text-decoration-none cursor-pointer"
        style={{
          ...styles.menuItem,
          ...(isHovered ? styles.menuItemHover : {}),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </a>
    )
  }

  return (
    <div
      onClick={handleClick}
      className="d-flex align-items-center cursor-pointer"
      style={{ ...styles.menuItem, ...(isHovered ? styles.menuItemHover : {}) }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </div>
  )
}

MenuItem.propTypes = {
  onClick: PropTypes.func,
  href: PropTypes.string,
  children: PropTypes.node.isRequired,
}

export const HighlightActionMenu = ({ hId, anchor, coursenum }) => {
  let [currentState, dispatch] = useStateValue()
  let [dropdownOpen, setDropdownOpen] = React.useState(false)

  const editHighlight = hId => {
    if (!currentState.editingIDs.includes(hId)) {
      dispatch({
        type: 'updateEditingIDs',
        payload: [...currentState.editingIDs, hId],
      })
    }
  }

  const openDeleteModal = hId => {
    if (!currentState.toDeleteIds.includes(hId)) {
      dispatch({
        type: 'updateToDeleteIds',
        payload: [...currentState.toDeleteIds, hId],
      })
    }
  }

  const handleMenuItemClick = callback => {
    callback()
    setDropdownOpen(false)
  }

  return (
    <div className="btn-group pull-right mt-1">
      <Dropdown
        trigger={
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="bg-white border-0 cursor-pointer"
            style={styles.triggerButton}
          >
            <i className="fa-solid fa-ellipsis color-me-darkgray" />
          </button>
        }
        isOpen={dropdownOpen}
        onClose={() => setDropdownOpen(false)}
      >
        <MenuItem href={`/reader/web/${coursenum}/${anchor}/`}>
          <div className="fa-utility fa-semibold fa-eye mr-2" />
          View
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick(() => editHighlight(hId))}>
          <div className="far fa-pencil mr-2" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuItemClick(() => openDeleteModal(hId))}
        >
          <div className="far fa-trash mr-2" />
          Delete
        </MenuItem>
      </Dropdown>
    </div>
  )
}

HighlightActionMenu.propTypes = {
  hId: PropTypes.string,
  coursenum: PropTypes.string,
  anchor: PropTypes.string,
}

const styles = {
  dropdownPanel: {
    top: '100%',
    right: '0',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    minWidth: '160px',
    zIndex: 1000,
  },
  menuItem: {
    padding: '8px 16px',
    color: '#333',
    transition: 'background-color 0.2s',
  },
  menuItemHover: {
    backgroundColor: '#f5f5f5',
  },
  triggerButton: {
    padding: '6px 12px',
    borderRadius: '4px',
  },
}
